import { IdentifyTraits, type RudderAnalytics } from '@rudderstack/analytics-js';

import { v4 as uuidv4 } from 'uuid';

import { getEnv } from '../utils/env';
import { captureError } from '../utils/errors';

let rudderAnalytics: RudderAnalytics | null = null;
let rudderStackCurrentTeam:
  | {
      internal: boolean;
      name: string;
    }
  | undefined;

export async function initRudderstack(defaultWriteKey?: string, defaultDataplaneUrl?: string) {
  const ignoreRuddestack = getEnv(
    'NEXT_PUBLIC_RUDDERSTACK_IGNORE',
    process.env.NEXT_PUBLIC_RUDDERSTACK_IGNORE,
  );

  if (ignoreRuddestack === 'true') {
    return;
  }

  const resolvedWriteKey = getEnv(
    'NEXT_PUBLIC_RUDDERSTACK_KEY',
    process.env.NEXT_PUBLIC_RUDDERSTACK_KEY,
    defaultWriteKey,
  );
  const resolvedDataplaneUrl = getEnv(
    'NEXT_PUBLIC_RUDDERSTACK_DATAPLANE_URL',
    process.env.NEXT_PUBLIC_RUDDERSTACK_DATAPLANE_URL,
    defaultDataplaneUrl,
  );

  if (!resolvedWriteKey || !resolvedDataplaneUrl) {
    return;
  }

  try {
    const { RudderAnalytics } = await import('@rudderstack/analytics-js');
    rudderAnalytics = new RudderAnalytics();
    rudderAnalytics.load(resolvedWriteKey, resolvedDataplaneUrl);

    document.addEventListener('click', trackButtonAndLinkClicks, true);
  } catch (error) {
    captureError(error);
  }
}

export function setRudderstackCurrentTeam(team?: { internal: boolean; name: string }) {
  rudderStackCurrentTeam = team;
}

function getTeamProperties() {
  if (!rudderStackCurrentTeam) {
    return {};
  }

  return {
    $groups: { team: rudderStackCurrentTeam.name },
    team: rudderStackCurrentTeam.name,
  };
}

export function trackRudderstackEvent(event: string, properties?: any) {
  if (!rudderAnalytics || rudderStackCurrentTeam?.internal) {
    return;
  }

  rudderAnalytics.track(event, {
    ...properties,
    ...getTeamProperties(),
    _event_id: uuidv4(),
  });
}

export function trackRudderstackPageView() {
  if (!rudderAnalytics || rudderStackCurrentTeam?.internal) {
    return;
  }

  rudderAnalytics.page(getTeamProperties());
}

export function identifyRudderstackUser(userId: string, traits: IdentifyTraits) {
  if (!rudderAnalytics) {
    return;
  }

  rudderAnalytics.identify(userId, traits);
}

export function groupRudderstackUser(groupId: string, traits: IdentifyTraits) {
  if (!rudderAnalytics) {
    return;
  }

  rudderAnalytics.group(groupId, traits);
}

export function anonymizeRudderstackUser() {
  if (!rudderAnalytics) {
    return;
  }

  // Instead of reset(), identify the anonymous user with RudderStack's anonymous ID
  // This maintains session continuity for later identification
  const anonId = rudderAnalytics.getAnonymousId();
  if (anonId) {
    rudderAnalytics.identify(anonId, {
      anonymous: true,
    });
  }
}

export function resetRudderstackUser() {
  if (!rudderAnalytics) {
    return;
  }

  rudderAnalytics.reset();
}

function trackButtonAndLinkClicks(event: MouseEvent) {
  const buttonOrLink: HTMLButtonElement | HTMLAnchorElement | null = (
    event.target as HTMLElement
  ).closest('button, a');

  if (buttonOrLink) {
    const payload: { disabled?: boolean; text: string } = {
      text: buttonOrLink.title || buttonOrLink.textContent || 'Unknown',
    };

    if (buttonOrLink instanceof HTMLButtonElement && buttonOrLink.disabled) {
      payload.disabled = true;
    }

    const eventName = buttonOrLink instanceof HTMLButtonElement ? 'buttonClick' : 'linkClick';
    trackRudderstackEvent(eventName, {
      ...payload,
      ...getTeamProperties(),
    });
  }
}
